import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Search } from '@mui/icons-material';
import { useEffect } from 'react';

import SearchTextField from 'Components/SeachTextField/SearchTextField';
import CustomSelect from 'Components/CustomSelect/CustomSelect';
import CustomDatePicker from 'Components/CustomDatePicker/CustomDatePicker';
import useTranslation from 'Hooks/useTranslation';
import useDebounce from 'Hooks/useDebounce';
import type { HistoryEmployeeReportFilterI } from 'types';
import { historyEmployeeReportFilterSchema, defaultValue } from '../../schemas/history-employee-report-schemas';
import {
  ReportsLayoutFiltersContainer,
  ResetButton,
  SearchButton,
} from '../HistoryReportFilters/HistoryReportFilters.styled';
import { formatDateToISO } from 'Utils/format-date';

interface Props {
  onSearch: (filters: HistoryEmployeeReportFilterI) => void;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Por Cursar' },
  { value: 'approved', label: 'Aprobado' },
  { value: 'failed', label: 'Reprobado' },
  { value: 'expired', label: 'Vencido' },
];

export const HistoryEmployeeReportFilter = ({ onSearch }: Props) => {
  const { t2 } = useTranslation('history_employee_report');

  const {
    control,
    reset,
    getValues,
    formState: { isDirty },
    watch,
  } = useForm({
    resolver: zodResolver(historyEmployeeReportFilterSchema),
    defaultValues: defaultValue,
  });

  const handleReset = () => {
    reset(defaultValue);
    onSearch({});
  };

  const getFiltersForApi = (): HistoryEmployeeReportFilterI => {
    const values = getValues();
    // Solo enviar los filtros de búsqueda, no page y limit
    const { name, assignedAt, status } = values;
    const filters: HistoryEmployeeReportFilterI = {
      name: name || undefined,
      assignedAt: formatDateToISO(assignedAt),
    };

    if (status) filters.status = status;

    return filters;
  };

  const nameValue = watch('name');
  const debouncedName = useDebounce(nameValue, 500);

  useEffect(() => {
    if (debouncedName !== undefined) {
      const values = getFiltersForApi();
      onSearch(values);
    }
  }, [debouncedName]);

  return (
    <ReportsLayoutFiltersContainer>
      <Controller
        name="name"
        control={control}
        render={({ field }) => <SearchTextField placeholder={t2('course_name_placeholder')} {...field} />}
      />

      <Controller
        name="assignedAt"
        control={control}
        render={({ field }) => <CustomDatePicker label={t2('assigned_date')} placeholder="dd/mm/yyyy" {...field} />}
      />

      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <CustomSelect ariaLabel={t2('status')} placeholder={t2('status')} options={STATUS_OPTIONS} {...field} />
        )}
      />

      <SearchButton
        variant="outlined"
        color="success"
        hidden={!isDirty}
        startIcon={<Search />}
        onClick={() => {
          const values = getFiltersForApi();
          onSearch(values);
        }}
      >
        {t2('search')}
      </SearchButton>

      {isDirty && (
        <ResetButton onClick={handleReset} variant="outlined">
          {t2('clear_filters')}
        </ResetButton>
      )}
    </ReportsLayoutFiltersContainer>
  );
};
